import * as Tooltip from '@radix-ui/react-tooltip'
import { rem } from 'polished'
import React, { forwardRef, useRef, useState } from 'react'
import { formatPrice } from 'shared-constants'
import styled from 'styled-components'
import { Divider } from 'components/Divider'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Flex } from 'components/Layout'
import { MarkdownRenderer } from 'components/MarkdownRenderer'
import { ReadMore } from 'components/ReadMore'
import { H6, Text } from 'components/Typography'
import { type CurrencyIsoCode } from 'constants/common'
import {
  BORDER_WIDTH,
  BOX_SHADOW,
  COLOR_INTENT,
  FONT_SIZE,
  FONT_STACK,
  RADIUS,
  SPACE,
  Z_INDEX,
} from 'Theme'
import { useMedia } from 'useMedia'

const TOOLTIP_MAX_WIDTH = rem(320)
const READ_MORE_COLLAPSED_HEIGHT = rem(100)

const Card = styled.article`
  background-color: ${COLOR_INTENT.SESSION_BOOKING_CARD.BACKGROUND};
  padding: ${SPACE.PX_16};
  border-radius: ${RADIUS.PX_10};
  border: ${BORDER_WIDTH.PX_1} solid ${COLOR_INTENT.SESSION_BOOKING_CARD.BORDER};
`

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE.PX_8};
`

const StyledText = styled(Text)`
  cursor: pointer;
`

const StyledTooltipContent = styled(Tooltip.Content)`
  max-width: ${TOOLTIP_MAX_WIDTH};
  padding: ${SPACE.PX_8} ${SPACE.PX_10};
  background-color: ${COLOR_INTENT.TOOLTIP.BACKGROUND};
  box-shadow: ${BOX_SHADOW.ELEVATION_1};
  border-radius: ${RADIUS.PX_6};
  z-index: ${Z_INDEX.TOOLTIP};
`

interface SessionBookingCardProps {
  title: string
  description: string
  expertName: string
  duration: number
  price?: {
    total: number
    currencyIsoCode: CurrencyIsoCode
  }
  displayPrice?: {
    total: number
    currencyIsoCode: CurrencyIsoCode
  }
  onDoubleClick?: () => void
}

export const SessionBookingCard = forwardRef<
  HTMLElement,
  React.PWC<SessionBookingCardProps>
>(
  (
    {
      title,
      description,
      expertName,
      duration,
      price,
      displayPrice,
      children,
      onDoubleClick,
      ...props
    },
    ref
  ) => {
    const readMoreWrapperRef = useRef(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const { MOBILE: isMobile } = useMedia()

    return (
      <Card ref={ref} {...props}>
        <Header>
          <Flex flexDirection="column" gap={SPACE.PX_4}>
            <H6 as="h4" mb="unset">
              {title}
            </H6>
            {/* Only private sessions are availible */}
            <Text
              color={COLOR_INTENT.SESSION_BOOKING_CARD.SUBTITLE}
              fontSize={FONT_SIZE.PX_12}
            >
              Private consultation
            </Text>
          </Flex>
          {!isMobile && (
            <Tooltip.Provider>
              <Tooltip.Root delayDuration={0}>
                <Tooltip.Trigger asChild>
                  <div>
                    <Icon.Info
                      color={COLOR_INTENT.SESSION_BOOKING_CARD.ICON.INFO}
                      size={ICON_SIZE.PX_20}
                    />
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <StyledTooltipContent>
                    <MarkdownRenderer
                      markdownSource={description}
                      fontFamily={FONT_STACK.SANS}
                      fontSize={FONT_SIZE.PX_12}
                      color={COLOR_INTENT.TEXT_ON_DARK}
                    />
                    <Tooltip.Arrow />
                  </StyledTooltipContent>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          )}
        </Header>
        <Divider
          color={COLOR_INTENT.SESSION_BOOKING_CARD.DIVIDER}
          opacity={0.5}
          my={SPACE.PX_8}
        />
        <main>
          <Flex flexDirection="column" gap={SPACE.PX_8}>
            {isMobile && (
              <ReadMore
                wrapperRef={readMoreWrapperRef}
                isExpanded={isExpanded}
                fontFamily={FONT_STACK.SERIF}
                fontSize={FONT_SIZE.PX_16}
                collapsedHeight={READ_MORE_COLLAPSED_HEIGHT}
                actionButtonWrapperProps={{ mb: SPACE.PX_12 }}
                actionButton={() => (
                  <StyledText
                    color={COLOR_INTENT.TEXT_ON_LIGHT}
                    fontSize={FONT_SIZE.PX_12}
                    onClick={() => {
                      setIsExpanded((prevState) => !prevState)
                    }}
                  >
                    {isExpanded ? 'Show less' : 'Read more'}
                  </StyledText>
                )}
              >
                <MarkdownRenderer
                  markdownSource={description}
                  color={COLOR_INTENT.SESSION_BOOKING_CARD.ICON.INFO}
                  fontSize={FONT_SIZE.PX_12}
                />
              </ReadMore>
            )}
            {Boolean(expertName) && (
              <Flex gap={SPACE.PX_8}>
                <div>
                  <Icon.User
                    color={COLOR_INTENT.SESSION_BOOKING_CARD.ICON.INFO}
                    size={ICON_SIZE.PX_16}
                  />
                </div>
                <Text>{expertName}</Text>
              </Flex>
            )}
            <Flex alignItems="center" gap={SPACE.PX_8}>
              <Icon.Time
                color={COLOR_INTENT.SESSION_BOOKING_CARD.ICON.INFO}
                size={ICON_SIZE.PX_16}
              />
              <Text>{duration} minutes</Text>
            </Flex>
            {price && (
              <Flex alignItems="center" gap={SPACE.PX_8}>
                <Icon.DollarOutlined
                  color={COLOR_INTENT.SESSION_BOOKING_CARD.ICON.INFO}
                  size={ICON_SIZE.PX_16}
                />
                <Text>
                  {formatPrice({
                    price: price.total,
                    currency: price.currencyIsoCode,
                  })}
                  {displayPrice?.currencyIsoCode !== price.currencyIsoCode &&
                    displayPrice && (
                      <>
                        {` (approx. ${formatPrice({
                          price: displayPrice.total,
                          currency: displayPrice.currencyIsoCode,
                        })})`}
                      </>
                    )}
                </Text>
              </Flex>
            )}
          </Flex>
          {children}
        </main>
      </Card>
    )
  }
)
